import { DecimalPipe } from '@angular/common';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DataTableComponent, TableColumn } from '@libs/front/components/data-table/data-table.component';
import { PanelComponent } from '@libs/front/components/panel/panel.component';
import { ScrollableComponent } from 'src/app/shared/components/scrollable/scrollable.component';
import { Blast } from 'src/app/services/hole.service';
import { BlastEnvironmentService } from 'src/app/services/blast-environment.service';
import { Co2BlastSummary } from 'src/app/services/co2-calculator.service';
import { DeviceService } from 'src/app/services/device.service';

@Component({
  selector: 'app-blast-environment',
  templateUrl: './blast-environment.component.html',
  styleUrls: ['./blast-environment.component.scss'],
  standalone: true,
  imports: [TranslateModule, DecimalPipe, DataTableComponent, PanelComponent, ScrollableComponent],
  providers: [DecimalPipe]
})
export class BlastEnvironmentComponent {
  private readonly blastEnvironmentService = inject(BlastEnvironmentService);
  private readonly decimalPipe = inject(DecimalPipe);
  readonly device = inject(DeviceService);

  readonly tirId = input<number>();
  readonly blast = input<Blast>();

  readonly co2Summary = signal<Co2BlastSummary | null>(null);
  readonly isLoading = signal(false);
  readonly hasLoaded = signal(false);

  readonly viewState = computed<'loading' | 'content' | 'empty'>(() => {
    if (this.co2Summary()) return 'content';
    if (this.isLoading() || !this.hasLoaded() || !this.blast()?.Holes) return 'loading';
    return 'empty';
  });

  readonly transportColumns: TableColumn[] = [
    { key: 'factoryName',      label: 'ENVIRONMENT.FACTORY' },
    { key: 'storageName',      label: 'ENVIRONMENT.STORAGE' },
    { key: 'factoryToStorage', label: 'ENVIRONMENT.FACTORY_STORAGE' },
    { key: 'storageToSite',    label: 'ENVIRONMENT.STORAGE_SITE' }
  ];

  readonly drillingColumns: TableColumn[] = [
    { key: 'drillType',     label: 'ENVIRONMENT.DRILL_TYPE' },
    { key: 'drilledMeters', label: 'ENVIRONMENT.DRILLED_METERS' },
    { key: 'co2PerUnit',    label: 'ENVIRONMENT.CO2_PER_METER' },
    { key: 'total',         label: 'ENVIRONMENT.TOTAL_CO2' }
  ];

  readonly productColumns: TableColumn[] = [
    { key: 'productName', label: 'ENVIRONMENT.PRODUCT_NAME' },
    { key: 'quantity',    label: 'ENVIRONMENT.QUANTITY' },
    { key: 'co2PerUnit',  label: 'ENVIRONMENT.CO2_PER_UNIT' },
    { key: 'total',       label: 'ENVIRONMENT.TOTAL_CO2' }
  ];

  readonly transportTableData = computed(() => {
    const t = this.co2Summary()?.transport;
    if (!t) return [{ factoryName: '-', storageName: '-', factoryToStorage: '-', storageToSite: '-' }];
    return [{
      factoryName:      t.factoryName,
      storageName:      t.storageName,
      factoryToStorage: this.decimalPipe.transform(t.factoryToStorage, '1.0-2') ?? '-',
      storageToSite:    this.decimalPipe.transform(t.storageToSite,    '1.0-2') ?? '-'
    }];
  });

  readonly drillingTableData = computed(() => {
    const rows = (this.co2Summary()?.drilling ?? []).map(d => ({
      drillType:     d.drillType,
      drilledMeters: this.decimalPipe.transform(d.drilledMeters, '1.0-0') ?? '-',
      co2PerUnit:    this.decimalPipe.transform(d.co2PerUnit,    '1.0-0') ?? '-',
      total:         this.decimalPipe.transform(d.total,         '1.0-0') ?? '-'
    }));
    return rows.length ? rows : [{ drillType: '-', drilledMeters: '-', co2PerUnit: '-', total: '-' }];
  });

  readonly detonationsTableData = computed(() => {
    const rows = (this.co2Summary()?.detonations ?? []).map(d => ({
      productName: d.productName,
      quantity:    (this.decimalPipe.transform(d.quantity, '1.0-0') ?? '-') + (d.isBulk ? ' (kg)' : ''),
      co2PerUnit:  this.decimalPipe.transform(d.co2PerUnit, '1.0-0') ?? '-',
      total:       this.decimalPipe.transform(d.total,      '1.0-0') ?? '-'
    }));
    return rows.length ? rows : [{ productName: '-', quantity: '-', co2PerUnit: '-', total: '-' }];
  });

  readonly productsTableData = computed(() => {
    const rows = (this.co2Summary()?.products ?? []).map(p => ({
      productName: p.productName,
      quantity:    (this.decimalPipe.transform(p.quantity, '1.0-0') ?? '-') + (p.isBulk ? ' (kg)' : ''),
      co2PerUnit:  this.decimalPipe.transform(p.co2PerUnit, '1.0-0') ?? '-',
      total:       this.decimalPipe.transform(p.total,      '1.0-0') ?? '-'
    }));
    return rows.length ? rows : [{ productName: '-', quantity: '-', co2PerUnit: '-', total: '-' }];
  });

  constructor() {
    effect(() => {
      const blast = this.blast();
      if (blast?.Holes) {
        this.tryCalculate();
      }
    });

    effect(() => {
      const tirId = this.tirId();
      if (tirId) {
        this.loadData();
      }
    });
  }

  private tryCalculate(): void {
    this.co2Summary.set(this.blastEnvironmentService.calculateSummary(this.blast()));
  }

  private async loadData(): Promise<void> {
    if (!this.tirId()) return;

    this.isLoading.set(true);

    try {
      await this.blastEnvironmentService.loadData();
    } catch (error) {
      console.error('Error loading CO2 data:', error);
    } finally {
      this.tryCalculate();
      this.hasLoaded.set(true);
      this.isLoading.set(false);
    }
  }

  getDonutGradient(): string {
    const summary = this.co2Summary();
    if (!summary || summary.total === 0) {
      return 'conic-gradient(#e5e7eb 0% 100%)';
    }

    let cumulativePercentage = 0;
    const segments: string[] = [];

    const categoriesWithValue = summary.categories.filter(cat => cat.value > 0);

    if (categoriesWithValue.length === 0) {
      return 'conic-gradient(#e5e7eb 0% 100%)';
    }

    categoriesWithValue.forEach((cat, index) => {
      const isLast = index === categoriesWithValue.length - 1;
      const endPercentage = isLast ? 100 : cumulativePercentage + cat.percentage;
      segments.push(`${cat.color} ${cumulativePercentage}% ${endPercentage}%`);
      cumulativePercentage = endPercentage;
    });

    return `conic-gradient(${segments.join(', ')})`;
  }
}
